import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Select, Space, Tag, Tabs, Modal,
  Form, Input, message, Checkbox, Tooltip, Badge, Descriptions
} from 'antd';
import {
  SearchOutlined, PlusOutlined, EyeOutlined,
  CloseCircleOutlined, ReloadOutlined, CheckCircleOutlined
} from '@ant-design/icons';
import apiClient from '../../../api/apiClient';
import { getCicloActual, getAniosAcademicos } from '../../../utils/cicloEscolar';

const { Option } = Select;
const { TextArea } = Input;

const CICLO = getCicloActual();

const estadoTag = (estado) => {
  const map = {
    PENDIENTE:   { color: 'orange',  label: 'Pendiente'   },
    EN_PROCESO:  { color: 'blue',    label: 'En Proceso'  },
    COMPLETADA:  { color: 'green',   label: 'Completada'  },
    CANCELADA:   { color: 'red',     label: 'Cancelada'   },
  };
  const cfg = map[estado] || { color: 'default', label: estado };
  return <Tag color={cfg.color}>{cfg.label}</Tag>;
};

const SolicitudesRectificacion = () => {
  const user = JSON.parse(localStorage.getItem('user')) || {};

  // ── Catálogos ─────────────────────────────────────────────────────────────
  const [grados, setGrados]       = useState([]);
  const [secciones, setSecciones] = useState([]);
  const [jornadas, setJornadas]   = useState([]);
  const [loadingCat, setLoadingCat] = useState(true);

  // ── Tab 1: Buscar alumno ──────────────────────────────────────────────────
  const [filtros, setFiltros] = useState({
    cicloEscolar: CICLO, idGrado: null, idSeccion: null, idJornada: null,
  });
  const [alumnos, setAlumnos]           = useState([]);
  const [loadingAlumnos, setLoadingAlumnos] = useState(false);
  const [alumnoSel, setAlumnoSel]       = useState(null);   // alumno seleccionado
  const [cursos, setCursos]             = useState([]);
  const [loadingCursos, setLoadingCursos] = useState(false);
  const [cursosSelec, setCursosSelec]   = useState([]);     // IdAsignacionDocente[]
  const [motivo, setMotivo]             = useState('');
  const [creando, setCreando]           = useState(false);

  // ── Tab 2: Listado de solicitudes ─────────────────────────────────────────
  const [solicitudes, setSolicitudes]       = useState([]);
  const [loadingSol, setLoadingSol]         = useState(false);
  const [detalleVisible, setDetalleVisible] = useState(false);
  const [detalle, setDetalle]               = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [cancelando, setCancelando]         = useState(false);

  useEffect(() => { cargarCatalogos(); }, []);

  const cargarCatalogos = async () => {
    try {
      const [g, s, j] = await Promise.all([
        apiClient.get('/grados'),
        apiClient.get('/secciones'),
        apiClient.get('/jornadas'),
      ]);
      setGrados((g.data.data || g.data || []).filter(x => x.IdGrado != null));
      setSecciones((s.data.data || s.data || []).filter(x => x.IdSeccion != null));
      setJornadas((j.data.data || j.data || []).filter(x => x.IdJornada != null));
    } catch { message.error('Error al cargar catálogos'); }
    finally { setLoadingCat(false); }
  };

  // ── Buscar alumnos ────────────────────────────────────────────────────────
  const buscarAlumnos = async () => {
    if (!filtros.idGrado || !filtros.idJornada) {
      message.warning('Selecciona al menos Jornada y Grado para buscar.');
      return;
    }
    setLoadingAlumnos(true);
    setAlumnoSel(null);
    setCursos([]);
    setCursosSelec([]);
    try {
      const params = { cicloEscolar: filtros.cicloEscolar };
      if (filtros.idGrado)   params.idGrado   = filtros.idGrado;
      if (filtros.idSeccion) params.idSeccion  = filtros.idSeccion;
      if (filtros.idJornada) params.idJornada  = filtros.idJornada;
      const res = await apiClient.get('/inscripciones/buscar', { params });
      setAlumnos(res.data.data || []);
      if ((res.data.data || []).length === 0) message.info('No se encontraron alumnos con esos filtros.');
    } catch { message.error('Error al buscar alumnos.'); }
    finally { setLoadingAlumnos(false); }
  };

  // ── Cargar cursos del alumno ──────────────────────────────────────────────
  const seleccionarAlumno = async (alumno) => {
    setAlumnoSel(alumno);
    setCursos([]);
    setCursosSelec([]);
    setMotivo('');
    setLoadingCursos(true);
    try {
      const res = await apiClient.get(
        `/solicitudes-calificacion/cursos-alumno/${alumno.IdAlumno}`,
        { params: { cicloEscolar: filtros.cicloEscolar } }
      );
      setCursos(res.data.data || []);
    } catch { message.error('Error al cargar cursos del alumno.'); }
    finally { setLoadingCursos(false); }
  };

  // ── Crear solicitud ───────────────────────────────────────────────────────
  const crearSolicitud = async () => {
    if (!motivo.trim()) { message.warning('Escribe un motivo para la solicitud.'); return; }
    if (cursosSelec.length === 0) { message.warning('Selecciona al menos un curso.'); return; }
    setCreando(true);
    try {
      await apiClient.post('/solicitudes-calificacion', {
        IdAlumno: alumnoSel.IdAlumno,
        IdInscripcion: alumnoSel.IdInscripcion,
        Motivo: motivo.trim(),
        CursosAutorizados: cursosSelec,
        CreadoPor: user.IdUsuario,
      });
      message.success('Solicitud creada. Los docentes han sido notificados.');
      setAlumnoSel(null);
      setCursos([]);
      setCursosSelec([]);
      setMotivo('');
      setAlumnos([]);
    } catch (e) {
      message.error(e.response?.data?.error || 'Error al crear la solicitud.');
    } finally { setCreando(false); }
  };

  // ── Cargar solicitudes ────────────────────────────────────────────────────
  const cargarSolicitudes = async () => {
    setLoadingSol(true);
    try {
      const res = await apiClient.get('/solicitudes-calificacion');
      setSolicitudes(res.data.data || []);
    } catch { message.error('Error al cargar solicitudes.'); }
    finally { setLoadingSol(false); }
  };

  // ── Ver detalle ───────────────────────────────────────────────────────────
  const verDetalle = async (id) => {
    setDetalleVisible(true);
    setDetalle(null);
    setLoadingDetalle(true);
    try {
      const res = await apiClient.get(`/solicitudes-calificacion/${id}`);
      setDetalle(res.data.data);
    } catch { message.error('Error al cargar el detalle.'); }
    finally { setLoadingDetalle(false); }
  };

  // ── Cancelar solicitud ────────────────────────────────────────────────────
  const cancelarSolicitud = (id) => {
    Modal.confirm({
      title: '¿Cancelar esta solicitud?',
      content: 'Esta acción no se puede deshacer.',
      okText: 'Sí, cancelar',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        setCancelando(true);
        try {
          await apiClient.put(`/solicitudes-calificacion/${id}/cancelar`, { ModificadoPor: user.IdUsuario });
          message.success('Solicitud cancelada.');
          cargarSolicitudes();
          if (detalle?.IdSolicitud === id) setDetalleVisible(false);
        } catch (e) {
          message.error(e.response?.data?.error || 'Error al cancelar.');
        } finally { setCancelando(false); }
      },
    });
  };

  // ── Columnas tabla alumnos ────────────────────────────────────────────────
  const colsAlumnos = [
    { title: 'Carnet', dataIndex: 'IdAlumno', key: 'id', width: 90 },
    { title: 'Alumno', key: 'nombre', render: (_, r) => `${r.Apellidos} ${r.Nombres}` },
    { title: 'Matrícula', dataIndex: 'Matricula', key: 'mat', width: 110 },
    { title: 'Grado', dataIndex: 'NombreGrado', key: 'grado' },
    { title: 'Sección', dataIndex: 'NombreSeccion', key: 'sec', width: 80, align: 'center' },
    { title: 'Jornada', dataIndex: 'NombreJornada', key: 'jornada' },
    {
      title: 'Seleccionar', key: 'sel', width: 110, align: 'center',
      render: (_, r) => (
        <Button
          type={alumnoSel?.IdAlumno === r.IdAlumno ? 'primary' : 'default'}
          size="small"
          onClick={() => seleccionarAlumno(r)}
        >
          {alumnoSel?.IdAlumno === r.IdAlumno ? 'Seleccionado' : 'Seleccionar'}
        </Button>
      ),
    },
  ];

  // ── Columnas tabla cursos ────────────────────────────────────────────────
  const colsCursos = [
    {
      title: '', key: 'check', width: 50, align: 'center',
      render: (_, r) => (
        <Tooltip title={r.YaTieneSolicitudActiva ? 'Ya existe una solicitud activa para este curso' : ''}>
          <Checkbox
            checked={cursosSelec.includes(r.IdAsignacionDocente)}
            disabled={r.YaTieneSolicitudActiva}
            onChange={(e) => {
              if (e.target.checked) setCursosSelec(p => [...p, r.IdAsignacionDocente]);
              else setCursosSelec(p => p.filter(x => x !== r.IdAsignacionDocente));
            }}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Curso', key: 'curso',
      render: (_, r) => (
        <Space>
          {r.NombreCurso}
          {r.YaTieneSolicitudActiva && (
            <Tag color="orange" style={{ fontSize: 11 }}>Solicitud activa</Tag>
          )}
        </Space>
      ),
    },
    { title: 'Docente', dataIndex: 'NombreDocente', key: 'docente' },
    { title: 'Grado', dataIndex: 'NombreGrado', key: 'grado' },
    { title: 'Sección', dataIndex: 'NombreSeccion', key: 'sec', width: 80, align: 'center' },
    { title: 'Jornada', dataIndex: 'NombreJornada', key: 'jornada' },
  ];

  // ── Columnas tabla solicitudes ────────────────────────────────────────────
  const colsSolicitudes = [
    { title: 'Alumno', dataIndex: 'NombreAlumno', key: 'alumno', width: 200 },
    { title: 'Grado', dataIndex: 'NombreGrado', key: 'grado' },
    { title: 'Sección', dataIndex: 'NombreSeccion', key: 'sec', width: 80, align: 'center' },
    { title: 'Estado', dataIndex: 'Estado', key: 'estado', width: 120, render: estadoTag },
    {
      title: 'Progreso', key: 'prog', width: 100, align: 'center',
      render: (_, r) => (
        <Tooltip title={`${r.CursosCompletados} de ${r.TotalCursos} cursos completados`}>
          <Badge
            count={`${r.CursosCompletados}/${r.TotalCursos}`}
            style={{ backgroundColor: r.CursosCompletados === r.TotalCursos ? '#52c41a' : '#1677ff' }}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Fecha', dataIndex: 'FechaCreado', key: 'fecha', width: 110,
      render: (f) => f ? f.split('T')[0].split('-').reverse().join('/') : '-',
    },
    {
      title: 'Acciones', key: 'acc', width: 140, align: 'center',
      render: (_, r) => (
        <Space>
          <Tooltip title="Ver detalle">
            <Button icon={<EyeOutlined />} size="small" onClick={() => verDetalle(r.IdSolicitud)} />
          </Tooltip>
          {r.Estado !== 'COMPLETADA' && r.Estado !== 'CANCELADA' && (
            <Tooltip title="Cancelar">
              <Button danger icon={<CloseCircleOutlined />} size="small"
                onClick={() => cancelarSolicitud(r.IdSolicitud)} loading={cancelando} />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card title={<span><PlusOutlined /> Solicitudes de Rectificación</span>}>
        <Tabs
          defaultActiveKey="nueva"
          onChange={(k) => { if (k === 'lista') cargarSolicitudes(); }}
          items={[
            // ── Tab 1: Nueva solicitud ──────────────────────────────────────
            {
              key: 'nueva',
              label: 'Nueva Solicitud',
              children: (
                <>
                  {/* Filtros de búsqueda */}
                  <Space wrap style={{ marginBottom: 16 }}>
                    <Select style={{ width: 90 }} value={filtros.cicloEscolar}
                      onChange={v => setFiltros(f => ({ ...f, cicloEscolar: v }))}>
                      {getAniosAcademicos().map(y => <Option key={y} value={y}>{y}</Option>)}
                    </Select>
                    <Select style={{ width: 140 }} placeholder="Jornada" allowClear
                      loading={loadingCat} value={filtros.idJornada}
                      onChange={v => setFiltros(f => ({ ...f, idJornada: v }))}>
                      {jornadas.map(j => <Option key={j.IdJornada} value={j.IdJornada}>{j.NombreJornada}</Option>)}
                    </Select>
                    <Select style={{ width: 190 }} placeholder="Grado" allowClear
                      loading={loadingCat} value={filtros.idGrado}
                      onChange={v => setFiltros(f => ({ ...f, idGrado: v, idSeccion: null }))}>
                      {grados.map(g => <Option key={g.IdGrado} value={g.IdGrado}>{g.NombreGrado}</Option>)}
                    </Select>
                    <Select style={{ width: 120 }} placeholder="Sección" allowClear
                      loading={loadingCat} value={filtros.idSeccion}
                      onChange={v => setFiltros(f => ({ ...f, idSeccion: v }))}>
                      {secciones.map(s => <Option key={s.IdSeccion} value={s.IdSeccion}>{s.NombreSeccion}</Option>)}
                    </Select>
                    <Button type="primary" icon={<SearchOutlined />}
                      loading={loadingAlumnos} onClick={buscarAlumnos}>
                      Buscar Alumnos
                    </Button>
                  </Space>

                  {/* Tabla de alumnos */}
                  {alumnos.length > 0 && (
                    <Card size="small" title="Selecciona el alumno" style={{ marginBottom: 16 }}>
                      <Table columns={colsAlumnos} dataSource={alumnos}
                        rowKey="IdAlumno" pagination={{ pageSize: 8 }} size="small" bordered
                        rowClassName={r => alumnoSel?.IdAlumno === r.IdAlumno ? 'ant-table-row-selected' : ''}
                      />
                    </Card>
                  )}

                  {/* Cursos + motivo */}
                  {alumnoSel && (
                    <Card
                      size="small"
                      title={`Cursos de ${alumnoSel.Apellidos} ${alumnoSel.Nombres} — ${alumnoSel.NombreGrado} ${alumnoSel.NombreSeccion}`}
                      style={{ marginBottom: 16 }}
                    >
                      <Table columns={colsCursos} dataSource={cursos}
                        rowKey="IdAsignacionDocente" pagination={false} size="small" bordered
                        loading={loadingCursos}
                        locale={{ emptyText: 'No se encontraron cursos para este alumno' }}
                      />

                      <div style={{ marginTop: 16 }}>
                        <strong>Motivo de la solicitud <span style={{ color: 'red' }}>*</span></strong>
                        <TextArea
                          rows={3}
                          placeholder="Ej: Alumno inscrito en segunda unidad, requiere calificaciones retroactivas..."
                          value={motivo}
                          onChange={e => setMotivo(e.target.value)}
                          style={{ marginTop: 8 }}
                          maxLength={500}
                          showCount
                        />
                      </div>

                      <div style={{ marginTop: 16, textAlign: 'right' }}>
                        <Space>
                          <span style={{ color: '#666', fontSize: 12 }}>
                            {cursosSelec.length} curso(s) seleccionado(s)
                          </span>
                          <Button
                            type="primary"
                            icon={<CheckCircleOutlined />}
                            loading={creando}
                            disabled={cursosSelec.length === 0 || !motivo.trim()}
                            onClick={crearSolicitud}
                          >
                            Crear Solicitud
                          </Button>
                        </Space>
                      </div>
                    </Card>
                  )}
                </>
              ),
            },

            // ── Tab 2: Listado ──────────────────────────────────────────────
            {
              key: 'lista',
              label: 'Solicitudes',
              children: (
                <>
                  <div style={{ marginBottom: 16, textAlign: 'right' }}>
                    <Button icon={<ReloadOutlined />} onClick={cargarSolicitudes} loading={loadingSol}>
                      Actualizar
                    </Button>
                  </div>
                  <Table
                    columns={colsSolicitudes}
                    dataSource={solicitudes}
                    rowKey="IdSolicitud"
                    loading={loadingSol}
                    pagination={{ pageSize: 15, showTotal: t => `Total: ${t}` }}
                    bordered
                    locale={{ emptyText: 'No hay solicitudes registradas' }}
                  />
                </>
              ),
            },
          ]}
        />
      </Card>

      {/* Modal detalle */}
      <Modal
        title={`Detalle — Solicitud #${detalle?.IdSolicitud || ''}`}
        open={detalleVisible}
        onCancel={() => setDetalleVisible(false)}
        footer={
          detalle && detalle.Estado !== 'COMPLETADA' && detalle.Estado !== 'CANCELADA' ? (
            <Button danger onClick={() => cancelarSolicitud(detalle.IdSolicitud)} loading={cancelando}>
              Cancelar Solicitud
            </Button>
          ) : null
        }
        width={750}
        loading={loadingDetalle}
      >
        {detalle && (
          <>
            <Descriptions size="small" bordered column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Alumno" span={2}>{detalle.NombreAlumno}</Descriptions.Item>
              <Descriptions.Item label="Grado">{detalle.NombreGrado} {detalle.NombreSeccion}</Descriptions.Item>
              <Descriptions.Item label="Jornada">{detalle.NombreJornada}</Descriptions.Item>
              <Descriptions.Item label="Estado" span={2}>{estadoTag(detalle.Estado)}</Descriptions.Item>
              <Descriptions.Item label="Motivo" span={2}>{detalle.Motivo}</Descriptions.Item>
              <Descriptions.Item label="Fecha">
                {detalle.FechaCreado?.split('T')[0].split('-').reverse().join('/')}
              </Descriptions.Item>
            </Descriptions>

            <Table
              size="small"
              bordered
              pagination={false}
              dataSource={detalle.cursosAutorizados || []}
              rowKey="IdSolicitudCurso"
              columns={[
                { title: 'Curso', dataIndex: 'NombreCurso', key: 'curso' },
                { title: 'Docente', dataIndex: 'NombreDocente', key: 'docente' },
                {
                  title: 'Estado', dataIndex: 'Estado', key: 'est', width: 120, align: 'center',
                  render: (e) => <Tag color={e === 'COMPLETADO' ? 'green' : 'orange'}>{e}</Tag>,
                },
                {
                  title: 'Completado', dataIndex: 'FechaCompletado', key: 'fc', width: 110,
                  render: (f) => f ? f.split('T')[0].split('-').reverse().join('/') : '-',
                },
              ]}
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default SolicitudesRectificacion;
